import { Box, CircularProgress, TextField } from '@mui/material';
import React, { useMemo, useState } from 'react';

import { SelectedPartType } from './MobilePartActionsDialog.props';
import { LoadingContainer, PartTypeSearchBox, PartTypeList, PartTypeOption, PartTypeName } from './MobilePartActionsDialog.styles';
import { PartTypeActionLiveSearchItem } from '../../../../../../@types/partType.type';
import { COLORS } from '../../../../../../configs/colors';

interface PartTypeSelectionStepProps {
  defaultResults: PartTypeActionLiveSearchItem[];
  isSearching: boolean;
  onSelect: (partType: SelectedPartType) => void;
}

export const PartTypeSelectionStep: React.FC<PartTypeSelectionStepProps> = ({
  defaultResults,
  isSearching,
  onSelect,
}) => {
  const [searchText, setSearchText] = useState('');

  const filteredResults = useMemo(() => {
    if (!searchText.trim()) return defaultResults;
    const lower = searchText.toLowerCase();
    return defaultResults.filter(
      (item) =>
        item.partTypeName?.toLowerCase().includes(lower) ||
        item.partTypeNumber?.toLowerCase().includes(lower) ||
        item.serial1?.toLowerCase().includes(lower)
    );
  }, [defaultResults, searchText]);

  const handleSelect = (item: PartTypeActionLiveSearchItem): void => {
    onSelect({
      partTypeId: item.partTypeId,
      partTypeName: item.partTypeName,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PartTypeSearchBox>
        <TextField
          fullWidth
          size="small"
          placeholder="Search part types..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          aria-label="Search part types"
        />
      </PartTypeSearchBox>

      <PartTypeList>
        {isSearching && defaultResults.length === 0 && (
          <LoadingContainer>
            <CircularProgress size={24} />
          </LoadingContainer>
        )}

        {filteredResults.length === 0 && !isSearching && (
          <Box sx={{ textAlign: 'center', py: 3, color: COLORS.Gray, fontSize: 14 }}>
            {searchText ? 'No matching part types' : 'No part types found'}
          </Box>
        )}

        {filteredResults.map((item) => (
          <PartTypeOption
            key={`${item.partTypeId}-${item.partId || 'type'}-${item.serial1 || ''}`}
            onClick={() => handleSelect(item)}
            variant="outlined"
          >
            <Box sx={{ fontWeight: 600, fontSize: 14 }}>
              {item.partTypeName}
            </Box>
            {item.partTypeNumber && (
              <PartTypeName>{item.partTypeNumber}</PartTypeName>
            )}
            {item.serial1 && (
              <PartTypeName>SN: {item.serial1}</PartTypeName>
            )}
            {item.locationName && (
              <PartTypeName>{item.locationName}</PartTypeName>
            )}
          </PartTypeOption>
        ))}
      </PartTypeList>
    </Box>
  );
};
